import type { ForecastVersion } from "../types"

const LAST_OPENED_KEY = "sales_forecasting_last_version_id"

/** The org's primary forecast: the primary whose plan kind is not `budget` (a budget has its own primary). */
export function primary_forecast_version(versions: ForecastVersion[]): ForecastVersion | undefined {
  return (
    versions.find((v: ForecastVersion) => v.is_primary && (v.plan_kind ?? "forecast") !== "budget") ||
    versions.find((v: ForecastVersion) => v.is_primary)
  )
}

/**
 * The version to open on load: the one open last time when it still exists (and is not
 * archived), else the primary forecast (not the primary budget), else the first. The page used to
 * reopen the primary every time, which made it easy to edit the wrong version after a reload.
 */
export function pick_initial_version(
  versions: ForecastVersion[],
  last_opened_id: string | null | undefined
): ForecastVersion | undefined {
  if (last_opened_id) {
    const remembered = versions.find((v: ForecastVersion) => v.id === last_opened_id && !v.archived_at)
    if (remembered) return remembered
  }
  return primary_forecast_version(versions) || versions[0]
}

export function read_last_opened_version_id(): string | null {
  try {
    return localStorage.getItem(LAST_OPENED_KEY)
  } catch {
    return null
  }
}

export function remember_last_opened_version_id(version_id: string): void {
  try {
    localStorage.setItem(LAST_OPENED_KEY, version_id)
  } catch {
    // Private windows and blocked storage: the page just reopens on the primary.
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function version(id: string, extra: Partial<ForecastVersion> = {}): ForecastVersion {
    return { id, name: id, is_primary: false, created_at: "", updated_at: "", ...extra } as ForecastVersion
  }

  describe("pick_initial_version", () => {
    const primary = version("primary", { is_primary: true })
    const test = version("test")

    it("reopens the version that was open last", () => {
      expect(pick_initial_version([primary, test], "test")).toBe(test)
    })

    it("falls back to the primary when the remembered one is gone or archived", () => {
      expect(pick_initial_version([primary, test], "deleted")).toBe(primary)
      expect(pick_initial_version([primary, version("old", { archived_at: "2026-01-01" })], "old")).toBe(primary)
      expect(pick_initial_version([primary, test], null)).toBe(primary)
    })

    it("prefers the primary forecast over the primary budget", () => {
      const budget = version("budget", { is_primary: true, plan_kind: "budget" })
      expect(pick_initial_version([budget, primary, test], null)).toBe(primary)
      expect(primary_forecast_version([budget, test])).toBe(budget)
    })

    it("takes the first version when nothing is primary", () => {
      expect(pick_initial_version([test, version("other")], undefined)).toBe(test)
      expect(pick_initial_version([], "x")).toBeUndefined()
    })
  })
}
